import * as XLSX from 'xlsx'
import type { ProduccionCabecera, PlanificacionSemanalRow } from '@/types'

/**
 * El Excel del Gantt: dos hojas, cabeceras y componentes.
 *
 * Antes sólo salían las cabeceras —una fila por orden— y con eso no se puede
 * decidir nada: lo que se mira para decidir es el material. La segunda hoja baja
 * al grano real del dato, que es **orden × componente**, con el saldo que ya
 * trae calculado el origen.
 *
 * Las dos hojas enseñan **las mismas órdenes que la pantalla**: si el usuario ha
 * filtrado, el fichero va filtrado. Un Excel que trae más de lo que se estaba
 * mirando se usa creyendo que es lo otro.
 *
 * NULL no es cero, también aquí: una celda numérica sin dato se queda **vacía**,
 * no a cero. El peso que no está en el maestro y el peso que vale cero no son lo
 * mismo, y en una hoja de cálculo la diferencia la nota quien suma la columna.
 */

/** Traductor mínimo: lo que necesitan las cabeceras de columna. */
type T = (key: string, opts?: Record<string, unknown>) => string

export interface FilasGantt {
  cabeceras: (string | number | null)[][]
  componentes: (string | number | null)[][]
}

const num = (v: number | undefined | null): number | null =>
  typeof v === 'number' && isFinite(v) ? v : null

/**
 * Las dos hojas como matrices, sin tocar el fichero.
 *
 * Separado de la escritura para poder fijarlo con tests: lo que se rompe en un
 * export es el contenido, no el `writeFile`.
 */
export function construirFilasGantt({
  producciones, planRows, isEmpresaAll, fmtArt, etiquetaEstado, t,
}: {
  /** Las órdenes YA filtradas: las que la pantalla está enseñando. */
  producciones: ProduccionCabecera[]
  /** Todas las filas de plan del rango; se recortan a las órdenes de arriba. */
  planRows: PlanificacionSemanalRow[]
  isEmpresaAll: boolean
  fmtArt: (code: string | null | undefined) => string
  etiquetaEstado: (s: number) => string
  t: T
}): FilasGantt {
  const e = (k: string) => t(`planning.excel.${k}`)

  const cabeceras: (string | number | null)[][] = [
    [
      ...(isEmpresaAll ? [e('company')] : []),
      e('production'), e('article'), e('description'),
      e('route'), e('status'),
      e('startDate'), e('endDate'),
      e('units'), e('unitsPending'),
      e('advancePct'), e('color'), e('style'),
      ...(isEmpresaAll ? [e('warehouse')] : []),
      e('workers'),
    ],
    ...producciones.map(p => {
      const u = Number(p.unidades)
      const pend = Number(p.unidadesPendientes)
      return [
        ...(isEmpresaAll ? [p.empresa] : []),
        p.produccion, fmtArt(p.articulo), p.nombreArticulo ?? '',
        p.routeid, etiquetaEstado(p.status),
        p.fechaInicio ?? '', p.fechaFin ?? '',
        u, pend,
        u > 0 ? Math.round((u - pend) / u * 100) : 0,
        p.inventcolorid ?? '', p.inventstyleid ?? '',
        ...(isEmpresaAll ? [p.inventlocationid ?? ''] : []),
        p.cantidadOperarios,
      ]
    }),
  ]

  // Sólo los componentes de las órdenes que salen arriba, y en su mismo orden:
  // las dos hojas se leen juntas, con la de cabeceras mandando.
  const posicion = new Map(producciones.map((p, i) => [p.produccion, i]))
  const deLaPantalla = planRows
    .filter(r => posicion.has(r.prodid))
    .sort((a, b) =>
      (posicion.get(a.prodid)! - posicion.get(b.prodid)!) ||
      a.itemid.localeCompare(b.itemid))

  const componentes: (string | number | null)[][] = [
    [
      ...(isEmpresaAll ? [e('company')] : []),
      e('production'), e('article'),
      e('component'), e('componentName'),
      // El color y el estilo no son decoración: el saldo del componente es por
      // variante, así que sin ellos dos filas del mismo código parecen repetidas
      // y no lo son.
      e('color'), e('style'),
      e('warehouse'),
      e('consumption'), e('reserved'), e('pending'),
      e('initialStock'), e('entriesUntilOrder'),
      e('availableBefore'), e('availableAfter'),
      e('stockBreak'), e('materialStatus'), e('statusDate'),
      e('otherWarehousesStock'), e('otherWarehouses'),
      e('nextEntryDate'), e('nextEntryQty'),
      e('startDate'), e('endDate'),
    ],
    ...deLaPantalla.map(r => {
      const cabecera = producciones[posicion.get(r.prodid)!]
      return [
        ...(isEmpresaAll ? [r.dataareaid] : []),
        r.prodid, fmtArt(cabecera?.articulo),
        fmtArt(r.itemid), r.nombreMaterial ?? '',
        r.inventcolorid ?? '', r.inventstyleid ?? '',
        r.inventlocationid ?? '',
        num(r.consumoEstimado), num(r.reserva), num(r.pendiente),
        num(r.stockInicial), num(r.entradasHastaOrden),
        num(r.disponibleAntes), num(r.disponibleDespues),
        // La marca del origen tal cual: aquí no se recalcula la rotura.
        //
        // Y por eso esta columna NO coincide con la pantalla desde que existe
        // PENDIENTE_CALIDAD: el origen pone `flag_rotura = 1` en esas filas, y
        // el Gantt las excluye del riesgo con `cuentaComoRotura` mientras que
        // aquí salen como rotura. Es deliberado — esta columna sirve para
        // cuadrar contra Fabric, y el estado va en la celda de al lado, así que
        // quien lea las dos ve de dónde viene la diferencia. Decidido el
        // 2026-10-06. Si algún día molesta, la solución es una segunda columna
        // derivada, no cambiar ésta: dejaría de reflejar el origen.
        r.flagRotura === 1 ? e('breakYes') : e('breakNo'),
        r.estadoMaterial ?? '', r.fechaEstado ?? '',
        num(r.stockOtrosSpain), r.almacenesOtrosSpain ?? '',
        r.fechaProximaEntradaCubre ?? '', num(r.qtyProximaEntradaCubre),
        r.fechaInicio ?? '', r.fechaFin ?? '',
      ]
    }),
  ]

  return { cabeceras, componentes }
}

/** Monta el libro y lo descarga. `sufijo` es el sello del rango exportado. */
export function exportarGanttExcel(
  filas: FilasGantt,
  { nombreHoja, nombreHojaComponentes, fichero, sufijo }: {
    nombreHoja: string
    nombreHojaComponentes: string
    fichero: string
    sufijo: string
  },
): void {
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(filas.cabeceras), nombreHoja)
  // La hoja de componentes va siempre, aunque venga vacía de filas: una pestaña
  // que aparece y desaparece según el filtro hace dudar de si el dato existe.
  XLSX.utils.book_append_sheet(
    wb, XLSX.utils.aoa_to_sheet(filas.componentes), nombreHojaComponentes,
  )
  XLSX.writeFile(wb, `${fichero}_${sufijo}.xlsx`)
}
